import React from 'react';
import Link from 'react-router-dom/Link';
// Material UI Imports
import { Tabs, Tab } from '@mui/material';
import LibraryIcon from '@mui/icons-material/Book';
import ReportsIcon from '@mui/icons-material/Assignment';
import RoomIcon from '@mui/icons-material/Room';
import TimerIcon from '@mui/icons-material/Timer';
import ListIcon from '@mui/icons-material/ViewList';
import DataIcon from '@mui/icons-material/InsertChart';
import BurstMode from '@mui/icons-material/BurstMode';
import Assignment from '@mui/icons-material/Assignment';
import ThumbsUpDown from '@mui/icons-material/ThumbsUpDown';
import LocationIcon from '@mui/icons-material/PinDrop';
import LotIcon from '@mui/icons-material/Home';
// Custom Imports
import { TasksIcon, TaskReportIcon, DivisionIcon, CommunityIcon } from 'global/components/PerformIcons';
import logo from 'assets/logo.svg';
import locationGif from 'assets/location-selection-demo.gif';
import { get } from 'global/lib/lodash';
import Image from 'global/components/image';

export const welcomeItems = (classes, opts) => [
  {
    title: 'Welcome',
    label: <></>,
    longText: (
      <>
        <div className={classes.performLogoWrapper}>
          <Image src={logo} className={classes.performLogo} alt="" />
        </div>
        <p>Welcome to PERFORM!</p>
        <p>
          We're committed to advancing the home building industry and making things easier for you. We hope you find PERFORM useful for your daily tasks, whether that's construction quality, jobsite
          safety, home readiness, or warranty.
        </p>
        <p>
          Click <em>next</em> to explore the available features.
        </p>
      </>
    ),
  },
  {
    title: 'Location',
    label: (
      <>
        <LocationIcon className={classes.icon} />
        <span className={classes.icon}>Location</span>
      </>
    ),
    longText: (
      <>
        <p>
          <strong>Set your location</strong> by using the menu in the upper right hand corner.
        </p>
        <div className={classes.gifWrapper}>
          <Image src={locationGif} className={classes.gif} alt="" />
        </div>
        <p>
          The site will show <strong>only what's relevant</strong> to your location, whether it's a{' '}
          <span className={classes.noWrap}>
            <DivisionIcon className={classes.inlineIcon} /> division
          </span>
          ,{' '}
          <span className={classes.noWrap}>
            <CommunityIcon className={classes.inlineIcon} /> community
          </span>
          , or{' '}
          <span className={classes.noWrap}>
            <LotIcon className={classes.inlineIcon} /> lot
          </span>
          . You can change your location at any time to change what you see.
        </p>
      </>
    ),
  },
];

export const homeItems = (classes, opts) => [
  {
    title: 'Tasks',
    link: '/tasks',
    label: (
      <>
        <TasksIcon className={classes.icon} />
        <span className={classes.icon}>Tasks</span>
      </>
    ),
    canDisplay: () => get(opts, 'nav.tasks'),
    text: 'Manage all ongoing tasks in one place',
    longText: (
      <>
        <p>
          Use <strong>tasks</strong> to help track and resolve issues on the jobsite. Take a picture, write a comment, and assign tasks to anyone in the system. They will be notified, and you can
          carry on with your day.
        </p>
        <p>Tasks can be created on-the-go or following an assessment, but they're all tracked in one place for quick access.</p>
        <Tabs color="primary" value="" centered>
          <Tab component={Link} to="/tasks/list" classes={{ root: classes.color }} icon={<RoomIcon />} label="by Location" />
          <Tab component={Link} to="/tasks/age" classes={{ root: classes.color }} icon={<TimerIcon />} label="by Age" />
        </Tabs>
        <p>
          By default, tasks are organized <em>by location</em>, but open tasks can also be viewed all together <em>by age</em>.
        </p>
      </>
    ),
  },
  {
    title: 'Assessments',
    link: '/reports',
    label: (
      <>
        <ReportsIcon className={classes.icon} />
        <span className={classes.icon}>Assessments</span>
      </>
    ),
    canDisplay: () => get(opts, 'nav.assessments'),
    text: 'Create and review assessments of construction quality',
    longText: (
      <>
        <p></p>
        <p>
          Create <strong>assessments</strong> for conducting audits or inspections. You can conduct an assessment on a single building, across a community or over an entire division.
        </p>
        <p>For items of concern, create tasks and assign them to ensure they get addressed.</p>
        <Tabs color="primary" value="" centered>
          <Tab component={Link} to="/reports/list" classes={{ root: classes.color }} icon={<ListIcon />} label="List" />
          <Tab component={Link} to="/reports/data" classes={{ root: classes.color }} icon={<DataIcon />} label="Data" />
        </Tabs>
        <p>The default view shows a list of assessments. Use the data tab to download observation data for analytics.</p>
      </>
    ),
  },
  {
    title: 'Library',
    link: '/library',
    canDisplay: () => get(opts, 'nav.library'),
    label: (
      <>
        <LibraryIcon className={classes.icon} />
        <span className={classes.icon}>Library</span>
      </>
    ),
    text: 'Browse relevant construction details, instruction, and training',
    longText: (
      <>
        <p></p>
        <p>
          Search a reference <strong>library</strong> of your company's performance expectations. Use filters or keywords to quickly find what you're looking for.
        </p>
        <p>Documents are organized by editions, and then by area of concern.</p>
        <p className={classes.flexIcons}>
          <TaskReportIcon color="primary" className={classes.icon} />
          <BurstMode color="primary" className={classes.icon} />
          <Assignment color="primary" className={classes.icon} />
          <ThumbsUpDown color="primary" className={classes.icon} />
        </p>
        <p>Although checklists are the most common type of documentation, many different types of documents can be housed in the system.</p>
      </>
    ),
  },
];

export default homeItems;
